import { type ReactElement, useRef, useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/_shared/components/ui/table';
import { cn } from '@/_shared/lib/utils';

import { useFittedChipCount } from '../hooks/useFittedChipCount';
import type { AudienceDefinition } from '../types';

type AudienceJoin = NonNullable<AudienceDefinition['expression']['joins']>[number];

/**
 * Read-only list of the related tables an audience joins to project their attributes (ACT2-783).
 *
 * The joins are authored by the assistant (after measuring each link as many-to-one) and carried through
 * the builder untouched — there is no editor control for them yet. This section is what makes them visible.
 *
 * Layout: the "Enriched attributes" heading and one line naming the audience's base table (the subject the
 * attributes are added to), then a bordered, fixed-layout table with one row per
 * join (the model allows up to `MAX_JOINS`; the design is for one): Table (28%) | Matched on (20%) | Columns
 * available. The table cell shows the table's last name segment, with its schema beneath; the match shows the key
 * once when both sides share it. Columns are outline chips on ONE line — as many as the cell actually fits,
 * measured, then a "+N" button that expands them to wrap. A chip's title is the column's full
 * `<table>__<column>` name, the one the destination mapping lists. Renders nothing without joins.
 */
export function JoinedTablesPanel({
  joins,
  entity,
}: {
  joins: AudienceJoin[] | undefined;
  /** The audience's base table (`expression.from`) — the subject the attributes are added to. */
  entity: string | undefined;
}): ReactElement | null {
  if (!joins || joins.length === 0) return null;
  // The table name as written, never singularised: `users`, `CUSTOMERS` and `dim_customer_v2` all read
  // correctly as "each <name> row", where a guessed "User" / "Customer" label would not.
  const subject = entity ? lastSegment(entity) : undefined;

  return (
    <section aria-labelledby="joined-tables-heading" className="mt-5">
      <h2 id="joined-tables-heading" className="mb-1 text-sm font-semibold text-foreground">
        Enriched attributes
      </h2>
      <p className="mb-2 text-xs text-muted-foreground">
        Added to each {subject ? <code className="text-xs text-foreground">{subject}</code> : 'member'} row from related
        tables. They don&rsquo;t change who is in the audience.
      </p>
      <div className="overflow-hidden rounded-xl border border-border shadow-sm">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="border-b border-border bg-grey-25">
              <TableHead className="w-[28%]">Table</TableHead>
              <TableHead className="w-[20%]">Matched on</TableHead>
              {/* "Available", not "sent": a joined column only leaves the warehouse once a sync maps it. */}
              <TableHead>Columns available</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {joins.map((join) => (
              <JoinRow key={join.table} join={join} subject={subject} />
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function JoinRow({ join, subject }: { join: AudienceJoin; subject: string | undefined }): ReactElement {
  const name = tableName(join);
  const schema = schemaOf(join);
  const match = join.entityColumn === join.joinColumn ? join.entityColumn : `${join.entityColumn} = ${join.joinColumn}`;
  return (
    <TableRow className="border-b border-border last:border-b-0">
      <TableCell className="px-3 py-2.5 align-top">
        <div className="truncate font-mono text-[0.8125rem] font-medium text-foreground" title={join.table}>
          {name}
        </div>
        {schema ? (
          <div className="truncate font-mono text-xs text-muted-foreground" title={schema}>
            {schema}
          </div>
        ) : null}
      </TableCell>
      <TableCell className="px-3 py-2.5 align-top">
        <div
          className="truncate font-mono text-xs text-foreground"
          title={`${subject ? `${subject}.` : ''}${join.entityColumn} = ${name}.${join.joinColumn}`}
        >
          {match}
        </div>
      </TableCell>
      <TableCell className="px-3 py-2.5 align-top whitespace-normal">
        <ColumnChips name={name} columns={join.columns} />
      </TableCell>
    </TableRow>
  );
}

const CHIP_CLASS = 'font-mono font-normal';
const MORE_CLASS =
  'rounded bg-muted px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-foreground outline-none hover:bg-grey-75 focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** The columns as chips: one line by default (as many as fit, then "+N"), all wrapping when expanded. */
function ColumnChips({ name, columns }: { name: string; columns: string[] }): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const fitted = useFittedChipCount(containerRef, measureRef, columns.length);
  const hidden = columns.length - fitted;
  const shown = expanded ? columns : columns.slice(0, fitted);

  return (
    <div ref={containerRef} className="relative min-w-0">
      {/* Off-screen copy of every chip and one sample button, measured to decide how many fit. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 left-0 flex flex-nowrap gap-1 whitespace-nowrap"
      >
        {columns.map((column) => (
          <Badge key={column} data-chip variant="outline" className={CHIP_CLASS}>
            {column}
          </Badge>
        ))}
        <span data-more className={MORE_CLASS}>
          +{columns.length}
        </span>
      </div>

      <div className={cn('flex items-start gap-1', expanded && 'flex-wrap')}>
        <ul
          aria-label={`Columns available from ${name}`}
          className={cn('flex min-w-0 gap-1', expanded ? 'flex-wrap' : 'flex-nowrap overflow-hidden')}
        >
          {shown.map((column) => (
            <li key={column}>
              <Badge variant="outline" className={CHIP_CLASS} title={`${name}__${column}`}>
                {column}
              </Badge>
            </li>
          ))}
        </ul>
        {hidden > 0 ? (
          <button
            type="button"
            className={MORE_CLASS}
            aria-expanded={expanded}
            aria-label={expanded ? 'Show fewer columns' : `Show ${String(hidden)} more columns`}
            onClick={() => {
              setExpanded((open) => !open);
            }}
          >
            {expanded ? 'Show less' : `+${String(hidden)}`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** The output prefix the compiler uses: the table's last name segment, as written. */
function tableName(join: AudienceJoin): string {
  return lastSegment(join.table);
}

/** A qualified table name's last segment (`audiences_demo.users` → `users`). */
function lastSegment(table: string): string {
  return table.split('.').at(-1) ?? table;
}

/** Everything before the table's last segment (`audiences_demo`, `db.schema`), or '' for a bare name. */
function schemaOf(join: AudienceJoin): string {
  const cut = join.table.lastIndexOf('.');
  return cut > 0 ? join.table.slice(0, cut) : '';
}
